import * as errorTypes from '@constants/errorType';
import { setGlobalIsLoading } from '@store/globalSlice';
import { addPreviousPageToken, clearPageTokens, setPageToken } from '../search/searchSlice';
import { setErrors, setErrorLoadingMessage, setOriginalErrors, setIsFetching } from './errorSlice';

const config = require('@root/config/config');
const queryString = require('query-string');

function base64_url_encode(data) {
    return btoa(encodeURIComponent(data)).replace(/\//g, '_').replace(/\+/g, '-').replace(/=/, '~');
}

function base64_url_decode(data) {
    return decodeURIComponent(atob(data.replace(/_/g, '/').replace(/-/g, '+').replace(/~/,'=')));
}

const returnJsonWithAuth = async (res, {authCode, redirect}) => {
    if (res.status === 401 || res.status === 403) {
        // Build token request body. Only include `code` if we have a fresh one
        // from the login redirect. Sending code:null can corrupt the shared
        // auth cookie and log the user out of admin + errors.
        const tokenBody = { clientId: config.cognitoClientId };
        if (authCode) {
            tokenBody.code = authCode;
            tokenBody.redirect = window.location.origin;
        }

        let result = await fetch(config.adminAuthApiHost + "token", {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(tokenBody),
            credentials: "include"
        });

        if (result.status === 401) {
            // Session truly expired — redirect to Cognito login.
            // Requires errors UI origin to be registered as a callback URL
            // in Cognito app client.
            window.location.href = config.tokenUrl + base64_url_encode(window.location.origin + window.location.pathname + window.location.search);
            return;
        }
        if (redirect) {
            window.location.href = base64_url_decode(redirect);
            return;
        }

        // Token refresh succeeded via cookie — signal caller to retry
        return { __retryRequest: true };
    }

    return res.json();
}

export const filterCurrentErrorsByKeyword = (keyword) => async (dispatch, getState) => {
    if (!keyword) {
        return;
    }

    keyword = keyword.trim();

    let currentErrors = getState().error.errors;

    let filteredErrors = [];

    currentErrors.forEach(error => {
        if (error.ErrorMessage.indexOf(keyword) >= 0) {
            filteredErrors.push(error);
        }
    ;})

    dispatch(setOriginalErrors(currentErrors));
    dispatch(setErrors(filteredErrors));

    dispatch(setGlobalIsLoading(false));
};

export const navigateToPageToken = () => async (dispatch, getState) => {
    var url = `${config.errorApiHost}errors?status=Error&pageToken=${getState().error.pageToken}&searchLimit=${getSearchLimit()}`;
    dispatch(fetchErrors(errorTypes.ERROR, encodeURI(url)));
};

export const nextPage = () => async (dispatch, getState) => {
    dispatch(navigateToPageToken());
};


const appendSearch = (url, key) => {
    var parsedQueryString = queryString.parse(window.location.search);
    if (parsedQueryString[key]) {
        url = url + `&${key}=${encodeURIComponent(parsedQueryString[key])}`
    }

    return url;
}

const getSearchLimit = () => {
    try {
        const stored = window.localStorage.getItem('searchLimit');
        return stored ? JSON.parse(stored) : config.searchLimit;
    } catch {
        return config.searchLimit;
    }
}

const createSearchUrl = () => {
    var url = `${config.errorApiHost}errors?`;
    url = appendSearch(url, "status");
    url += `&searchLimit=${getSearchLimit()}`;
    url = appendSearch(url, "pageToken");
    url = appendSearch(url, "companyId");
    url = appendSearch(url, "orderId");
    url = appendSearch(url, "cs2System");

    return url;
};


export const fetchErrors = (preserveTokens) => async (dispatch, getState) => {
    // Prevent concurrent fetches (polling can overlap with in-flight requests)
    if (getState().error.isFetching) return;
    dispatch(setIsFetching(true));
    dispatch(setGlobalIsLoading(true));
    dispatch(setErrorLoadingMessage(null));

    var parsedQueryString = queryString.parse(window.location.search);

    if (!parsedQueryString.pageToken) {
        dispatch(setPageToken(null));
        dispatch(clearPageTokens());
    }

    var searchUrl = createSearchUrl();

    const maxRetries = 3;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            let authResponse = await fetch(searchUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': 'AnyVal'
                },
                credentials: "include"
            });

            let authRequestPayload = {
                authCode: getState().auth.authCode,
                redirect: getState().search.redirect
            };

            let jsonResponse = await returnJsonWithAuth(authResponse, authRequestPayload);

            // returnJsonWithAuth returns undefined when it triggered a redirect (session expired).
            // Page is navigating away — exit entirely.
            if (!jsonResponse) {
                dispatch(setIsFetching(false));
                dispatch(setGlobalIsLoading(false));
                return;
            }

            // Token was refreshed via cookie — retry the request on next iteration
            if (jsonResponse.__retryRequest) {
                continue;
            }

            dispatch(setErrors(jsonResponse.Errors));

            if (jsonResponse.PageToken && jsonResponse.PageToken !== "{}") {
                dispatch(setPageToken(jsonResponse.PageToken));
                dispatch(addPreviousPageToken(jsonResponse.PageToken));
            }

            dispatch(setIsFetching(false));
            dispatch(setGlobalIsLoading(false));
            return;
        }

        catch(error) {
            console.warn(`fetchErrors attempt ${attempt + 1}/${maxRetries} failed:`, error);
            if (attempt < maxRetries - 1) {
                await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
            } else {
                console.error('fetchErrors failed after retries:', error);
                dispatch(setErrorLoadingMessage(error));
            }
        }
    }

    // If the loop ended without returning (e.g. all attempts were token refreshes),
    // surface an error so the user knows something went wrong.
    if (!getState().error.errorLoadingMessage) {
        dispatch(setErrorLoadingMessage({ message: 'Unable to load errors. Please refresh or log in again.' }));
    }

    dispatch(setIsFetching(false));
    dispatch(setGlobalIsLoading(false));
};

export const submitErrorResolution = async (error, status) => {
    let url = `${config.errorApiHost}error/${error.CorrelationId}/resolve`;
    try {
        return fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': 'AnyVal'
            },
            credentials: "include",
            body: JSON.stringify({
                "Status": status
            })
        });
    }
    catch(error) {
        debugger
    }

    console.log("submit resolution response received");
}

export const resubmitError = async (error) => {
    let url = `${config.errorApiHost}error/${error.CorrelationId}/resubmit`;
    let resubmitResolutionResponse = await fetch(url, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'AnyVal'
        },
        credentials: "include"
    });

    const responseJson = await resubmitResolutionResponse.json();
    console.log(responseJson);
    return responseJson;
}

export const bulkResolve = (status) => async (dispatch, getState) => {
    let url = `${config.errorApiHost}error/bulkResolve`;
    let body = {
        Status: status,
        CorrelationIds: []
    };

    getState().error.checkedErrors.forEach(error => {
        body.CorrelationIds.push(error.CorrelationId);
    });

    let bulkResolutionResponse = await fetch(url, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'AnyVal'
        },
        credentials: "include",
        body: JSON.stringify(body)
    });

    console.log("bulkResolved response received");
    console.log(bulkResolutionResponse);

    return true;
}

export const sampleFunction = (status) => async (dispatch, getState) => {

    return true;
}

export const bulkResubmit = () => async (dispatch, getState) => {
    let url = `${config.errorApiHost}error/bulkResubmit`;
    let body = {
        CorrelationIds: []
    };

    getState().error.checkedErrors.forEach(error => {
        body.CorrelationIds.push(error.CorrelationId);
    });

    let bulkResolutionResponse = await fetch(url, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'AnyVal'
        },
        credentials: "include",
        body: JSON.stringify(body)
    });

    const responseJson = await bulkResolutionResponse.json();

    console.log("bulkResubmit response received");
    console.log(bulkResolutionResponse);
    console.log(responseJson);

    return responseJson;
};
